// Configuratore "Profili su misura" (redesign 2026-10, mockup "Profili su misura").
// Prezzo, codice ed "Esplodi codice" vengono da profileConfigLogic.js (stessa logica di prima,
// verificata su 5000 configurazioni). Riga del carrello, alimentatore consigliato, accessori e
// scheda PDF della configurazione sono identici a prima. Nuovo: scelte "confermate" / "predefinite".
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import '../css/CatalogoPage.css';
import '../css/ShopPage.css'; // pulsante PDF e quantità (shopUi)
import '../css/ConfiguratorPage.css';
import successSound from '../assets/success.mp3';
import { FiCheck, FiChevronLeft, FiCopy, FiEdit2, FiMinus, FiPlus, FiShoppingCart, FiZoomIn } from 'react-icons/fi';
import { urlAsset } from './utils';
import { useLanguage } from '../context/LanguageContext';
import { AddedToast, PdfButton, QtyStepper, formatEuro } from './shopUi';
import {
  computeListPrice,
  decodeProfileCode,
  formatLedLabel,
  generateCode,
  getConfiguredTotalPowerW,
  getModelLedColors,
  getModelMaxLength,
  getModelPowerOptions,
  getModelCode,
  getPowerPriceKey,
  getPowerWattsPerMeter,
  getSuggestedPowerSupply,
  isValidLength,
  roundCurrency,
  toNumber,
} from './profileConfigLogic';

const getGroupLabels = (t) => ({
  all: t('configurator.groupAll'),
  group1: t('configurator.groupSurface'),
  group2: t('configurator.groupRecessed'),
  group3: t('configurator.groupSuspended'),
});

const getCustomProfileCardImage = (model) => model.cardImage || model.thumbImage || model.image;
// Foto per finitura (finishImages in config.json), altrimenti la foto principale del modello.
const getFinishImage = (model, finish) => model?.finishImages?.[finish] || model?.image;

const FINISH_COLORS = { grigio: '#9AA1A8', nero: '#15181C', bianco: '#FFFFFF', champagne: '#D8C3A0' };
const LED_SWATCH = {
  2700: '#FFC98A', 3000: '#FFD8A0', 4000: '#FFF1D6', 5000: '#E5F0FF',
  RGB: 'linear-gradient(90deg,#ff4d4d,#ffd84d,#4dff88,#4db8ff,#b44dff)',
  RGBW: 'linear-gradient(90deg,#ff4d4d,#4dff88,#4db8ff,#fff)',
  CCT: 'linear-gradient(90deg,#FFC98A,#FFF1D6,#DCEBFF)',
};
const SPECIAL_LED = ['RGB', 'RGBW', 'CCT'];
const CABLES = ['1', '2', '3', '5', '10'];
const CABLE_EXITS = ['SX', 'DX', 'SX BACK', 'DX BACK', 'CENTRO BACK'];

// Disegnino dell'uscita cavo (profilo visto di fronte)
const ExitIcon = ({ exit }) => {
  const x = { SX: 6, DX: 40, 'SX BACK': 10, 'DX BACK': 36, 'CENTRO BACK': 23 }[exit];
  const back = exit.includes('BACK');
  return (
    <svg viewBox="0 0 46 18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <rect x="6" y="4" width="34" height="7" rx="2" />
      {back
        ? <><path d={`M${x} 11v6`} /><circle cx={x} cy="11" r="1.6" fill="currentColor" /></>
        : <path d={exit === 'SX' ? 'M6 7.5H0' : 'M40 7.5H46'} />}
    </svg>
  );
};

// Scelte iniziali di un modello (come prima: grigio se c'è, altrimenti bianco; luce 3000K se c'è; 300 cm; cavo 1 m SX)
const defaultSelections = (configData, model) => {
  const leds = getModelLedColors(configData, model.name);
  const ledColor = leds.includes('3000') ? '3000' : leds[0];
  const firstPower = getModelPowerOptions(configData, model.name, ledColor)[0] || null;
  const finishes = model.availableFinishes || [];
  const profileColor = finishes.includes('grigio') ? 'grigio' : finishes.includes('bianco') ? 'bianco' : finishes[0];
  const covers = configData.modelCoverCompatibility?.[model.name] || [];
  return {
    profileColor,
    length: String(Math.min(300, getModelMaxLength(configData, model.name))),
    ledColor,
    power: firstPower ? firstPower.power : '',
    voltage: firstPower ? firstPower.voltage : '',
    cover: covers[0] || '',
    cableLength: '1',
    cableExit: 'SX',
    dimmerTouch: 'no',
    powerSupply: '24V',
    emissionOption: 'monoemissione',
  };
};

const Configurator = ({ showPrices = true }) => {
  const { addToCart, replaceCartItem } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const GROUP_LABELS = getGroupLabels(t);
  const [configData, setConfigData] = useState({});
  const [group, setGroup] = useState('all');
  const [selectedModel, setSelectedModel] = useState(null);
  const [view, setView] = useState('list');
  const [sel, setSel] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [powerSupplies, setPowerSupplies] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [codeImportValue, setCodeImportValue] = useState('');
  const [codeImportMessage, setCodeImportMessage] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const [accessoryQuantities, setAccessoryQuantities] = useState({});
  const [touched, setTouched] = useState(() => new Set());
  const [askConfirm, setAskConfirm] = useState(false);
  const [imageKind, setImageKind] = useState('photo');
  const [zoomSrc, setZoomSrc] = useState(null);
  const [copied, setCopied] = useState(false);
  const [flashCode, setFlashCode] = useState(false);
  const [editing, setEditing] = useState(null); // riga del carrello aperta con "Modifica"
  const stepRefs = useRef({});
  const prevCode = useRef('');

  // Il config viene sempre caricato dal server (nuovi modelli/prezzi subito visibili).
  useEffect(() => {
    fetch('/api/products/config/config')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => setConfigData(data))
      .catch(error => console.error('Error loading configuration file:', error));
  }, []);

  useEffect(() => {
    fetch('/api/products/config/alimentatori')
      .then(res => res.json())
      .then(data => {
        const only24V = data.filter(
          a => a.active && a.category === '24v'
        );
        setPowerSupplies(only24V);
      });
  }, []);

  useEffect(() => {
    if (!successMessage) return undefined;
    const timer = setTimeout(() => setSuccessMessage(''), 2400);
    return () => clearTimeout(timer);
  }, [successMessage]);

  useEffect(() => {
    if (!warningMessage) return undefined;
    const timer = setTimeout(() => setWarningMessage(''), 3000);
    return () => clearTimeout(timer);
  }, [warningMessage]);

  useEffect(() => {
    if (!zoomSrc) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setZoomSrc(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [zoomSrc]);

  // "Modifica" dal carrello: riapre il modello della riga con le sue scelte, quantità e note.
  // Le scelte vengono da datasheetConfig (salvato con la riga); per righe senza, dal codice.
  useEffect(() => {
    const item = location.state?.editItem;
    if (!item || !configData.models) return;
    navigate(location.pathname, { replace: true, state: null });
    const all = Object.entries(configData.models).flatMap(([groupKey, list]) =>
      (list || []).map((m) => ({ ...m, groupKey, groupLabel: GROUP_LABELS[groupKey] || groupKey })));
    const cfg = item.datasheetConfig || {};
    const code = String(item.code || '').toUpperCase();
    const target = all.find((m) => m.name === cfg.modelName) ||
      all.filter((m) => code.startsWith(getModelCode(configData, m.name)))
        .sort((a, b) => getModelCode(configData, b.name).length - getModelCode(configData, a.name).length)[0];
    if (!target) {
      showWarningMessage(t('pcfg.editNotFound'));
      return;
    }
    const exitMatch = String(item.notes || '').match(/^(?:Uscita cavo|Cable exit|Kabelausgang): *(.+)$/m);
    const cleanNotes = String(item.notes || '').replace(/^(?:Uscita cavo|Cable exit|Kabelausgang): *.+$/m, '').trim();
    let next = null;
    if (cfg.modelName && cfg.powerPerMeter) {
      next = {
        profileColor: cfg.profileColor,
        length: String(cfg.length),
        ledColor: cfg.ledColor,
        power: cfg.powerPerMeter,
        voltage: cfg.voltage,
        cover: cfg.cover,
        cableLength: String(cfg.cableLength),
        cableExit: cfg.cableExit || exitMatch?.[1]?.trim() || 'SX',
        dimmerTouch: cfg.dimmerTouch || 'no',
        powerSupply: cfg.powerSupply || '24V',
        emissionOption: cfg.emissionOption || 'monoemissione',
      };
    } else {
      const decoded = decodeProfileCode(configData, target, item.code);
      if (!decoded.error) {
        const v = decoded.value;
        next = {
          profileColor: v.profileColor,
          length: String(v.length),
          ledColor: v.ledColor,
          power: v.powerOption.power,
          voltage: v.powerOption.voltage,
          cover: v.cover,
          cableLength: String(v.cableLength),
          cableExit: exitMatch?.[1]?.trim() || 'SX',
          dimmerTouch: v.dimmerTouch,
          powerSupply: v.powerSupply,
          emissionOption: v.emissionOption,
        };
      }
    }
    handleSelectModel(target);
    setEditing(item);
    setQuantity(Math.max(1, parseInt(item.quantity, 10) || 1));
    setNotes(cleanNotes);
    if (next) {
      setSel(next);
      setTouched(new Set(['finish', 'emission', 'led', 'power', 'length', 'cover', 'cable', 'dimmer', 'psu']));
    } else {
      showWarningMessage(t('pcfg.editNotFound'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configData, location.state]);

  const cancelEdit = () => {
    setEditing(null);
    navigate('/cart');
  };

  // Elenco modelli per gruppo (extraGroups: modello visibile anche in altri filtri, es. BMN)
  const models = useMemo(() => {
    const sourceModels = configData.models;
    if (!sourceModels) return [];
    if (group === 'all') {
      return Object.entries(sourceModels).flatMap(([groupKey, groupItems]) =>
        (groupItems || []).map((model) => ({ ...model, groupKey, groupLabel: GROUP_LABELS[groupKey] || groupKey })));
    }
    return [
      ...(sourceModels[group] || []),
      ...Object.entries(sourceModels)
        .filter(([groupKey]) => groupKey !== group)
        .flatMap(([, groupItems]) => (groupItems || []).filter((m) => m.extraGroups?.includes(group))),
    ].map((model) => ({ ...model, groupKey: group, groupLabel: GROUP_LABELS[group] || group }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configData, group, t]);
  const groupCount = (key) => {
    const sourceModels = configData.models || {};
    if (key === 'all') return Object.values(sourceModels).flat().length;
    return (sourceModels[key] || []).length +
      Object.entries(sourceModels).filter(([g]) => g !== key).flatMap(([, l]) => l || []).filter((m) => m.extraGroups?.includes(key)).length;
  };

  const getProfileColorLabel = (color) => {
    switch (color) {
      case 'nero':
        return t('configurator.black');
      case 'bianco':
        return t('configurator.white');
      case 'grigio':
        return t('configurator.grey');
      default:
        return color ? color.charAt(0).toUpperCase() + color.slice(1) : '';
    }
  };

  const getEmissionLabel = (option) => {
    if (option === 'biemissione') return t('configurator.biEmission');
    if (option === 'monoemissione') return t('configurator.monoEmission');
    return option;
  };

  const composeNotesWithCableExit = (baseNotes, selectedCableExit) => {
    const cleanNotes = String(baseNotes || '').trim();
    if (!selectedCableExit) return cleanNotes;
    const exitLine = `${t('configurator.cableExitNotesPrefix')}: ${selectedCableExit}`;
    return cleanNotes ? `${cleanNotes}\n${exitLine}` : exitLine;
  };

  // ---------- Scelte effettive (stesse regole di prima) ----------
  const model = selectedModel;
  const modelName = model?.name;
  const covers = (model && configData.modelCoverCompatibility?.[modelName]) || [];
  const ledColors = model ? getModelLedColors(configData, modelName) : [];
  const powerOptions = model && sel ? getModelPowerOptions(configData, modelName, sel.ledColor) : [];
  const maxLength = model ? getModelMaxLength(configData, modelName) : 500;
  const dimmerAvailable = !!(model?.hasDimmerTouchOption && sel && !SPECIAL_LED.includes(sel.ledColor));
  const lengthNumber = sel ? parseInt(sel.length, 10) : NaN;
  const lengthOk = !!(model && Number.isFinite(lengthNumber) && isValidLength(configData, modelName, lengthNumber));

  const selections = useMemo(() => {
    if (!model || !sel || !sel.profileColor || !sel.power) return null;
    let powerSupply = model.hasPowerSupplyOption ? sel.powerSupply : '24V';
    if (sel.voltage === '230Vac') powerSupply = '230Vac';
    return {
      model: model.name,
      profileColor: sel.profileColor,
      length: lengthNumber,
      ledColor: sel.ledColor,
      power: sel.power,
      voltage: sel.voltage,
      cover: sel.cover,
      cableLength: sel.cableLength,
      cableExit: sel.cableExit,
      dimmerTouch: dimmerAvailable ? sel.dimmerTouch : 'no',
      powerSupply,
      emissionOption: model.hasEmissionOption ? sel.emissionOption : 'monoemissione',
      notes: composeNotesWithCableExit(notes, sel.cableExit),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, sel, notes, dimmerAvailable, lengthNumber, t]);

  const summary = useMemo(() => {
    if (!selections || !lengthOk) return null;
    const totalPowerW = getConfiguredTotalPowerW(selections);
    const { listinoUnit } = computeListPrice(configData, selections);
    const netPrice = roundCurrency(listinoUnit * (1 - (toNumber(user?.discount) || 0) / 100));
    const code = generateCode(
      configData,
      selections.model,
      selections.profileColor,
      selections.length,
      selections.ledColor,
      selections.power,
      selections.cover,
      selections.cableLength,
      selections.dimmerTouch,
      selections.powerSupply,
      selections.emissionOption
    );
    const shouldSuggestPowerSupply = selections.voltage === '24V' && selections.powerSupply === '24V';
    return {
      code,
      totalPowerW,
      listinoUnit,
      netPrice,
      suggestedPowerSupply: shouldSuggestPowerSupply ? getSuggestedPowerSupply(totalPowerW, powerSupplies) : null,
      suggestedRequiredW: shouldSuggestPowerSupply ? Math.round(totalPowerW * 1.15) : null,
    };
  }, [selections, lengthOk, configData, user, powerSupplies]);

  // il codice si illumina un attimo quando cambia
  useEffect(() => {
    if (!summary?.code) return undefined;
    if (prevCode.current && prevCode.current !== summary.code) {
      setFlashCode(true);
      const timer = setTimeout(() => setFlashCode(false), 600);
      prevCode.current = summary.code;
      return () => clearTimeout(timer);
    }
    prevCode.current = summary.code;
    return undefined;
  }, [summary]);

  // ---------- Azioni ----------
  const markTouched = (stepId) => {
    setTouched((prev) => (prev.has(stepId) ? prev : new Set(prev).add(stepId)));
    setAskConfirm(false);
  };
  const update = (stepId, patch) => {
    markTouched(stepId);
    setSel((prev) => ({ ...prev, ...patch }));
  };
  const pickLedColor = (ledColor) => {
    const options = getModelPowerOptions(configData, modelName, ledColor);
    const keep = options.find((o) => o.power === sel.power && o.voltage === sel.voltage) || options[0] || null;
    update('led', {
      ledColor,
      power: keep ? keep.power : '',
      voltage: keep ? keep.voltage : '',
      dimmerTouch: SPECIAL_LED.includes(ledColor) ? 'no' : sel.dimmerTouch,
    });
  };
  const changeLength = (value) => update('length', { length: String(value) });
  const stepLength = (delta) => {
    const current = Number.isFinite(lengthNumber) ? lengthNumber : 300;
    const next = Math.min(maxLength, Math.max(30, Math.round((current + delta) / 5) * 5));
    changeLength(next);
  };

  const handleSelectModel = (nextModel) => {
    setSelectedModel(nextModel);
    setSel(defaultSelections(configData, nextModel));
    setQuantity(1);
    setAccessoryQuantities({});
    setTouched(new Set());
    setAskConfirm(false);
    setImageKind('photo');
    setCodeImportValue('');
    setCodeImportMessage('');
    setView('detail');
    window.scrollTo({ top: 0 });
  };

  const handleBackToList = () => {
    setView('list');
  };

  const playSound = () => {
    const audio = new Audio(successSound);
    audio.play();
  };

  const handleDownload = (datasheet) => {
    if (!datasheet) return;
    // urlAsset punta a goooled.it (dominio diverso da b2b.goooled.com): l'attributo
    // download di un <a> viene ignorato cross-origin, quindi apriamo in una nuova
    // scheda per non far navigare via il B2B dalla pagina corrente.
    window.open(urlAsset(datasheet), '_blank', 'noopener,noreferrer');
  };

  const showMessage = (message) => {
    setWarningMessage('');
    setSuccessMessage(message);
  };

  const showWarningMessage = (message) => {
    setSuccessMessage('');
    setWarningMessage(message);
  };

  const applyDecodedCode = () => {
    const decoded = decodeProfileCode(configData, selectedModel, codeImportValue);
    if (decoded.error) {
      setCodeImportMessage(decoded.error);
      return;
    }
    const next = decoded.value;
    setSel((prev) => ({
      ...prev,
      profileColor: next.profileColor,
      length: String(next.length),
      ledColor: next.ledColor,
      power: next.powerOption.power,
      voltage: next.powerOption.voltage,
      cover: next.cover,
      cableLength: next.cableLength,
      dimmerTouch: next.dimmerTouch,
      powerSupply: next.powerSupply,
      emissionOption: next.emissionOption,
    }));
    // un codice incollato conferma tutte le scelte che descrive
    setTouched(new Set(['finish', 'emission', 'led', 'power', 'length', 'cover', 'cable', 'dimmer', 'psu']));
    setAskConfirm(false);
    setCodeImportMessage('Codice applicato alle selezioni.');
  };

  const buildPdfPayload = () => {
    if (!selections || !summary) return null;
    return {
      code: summary.code,
      modelName: selections.model,
      profileColor: selections.profileColor,
      length: selections.length,
      ledColor: selections.ledColor,
      totalPowerW: selections.power ? getConfiguredTotalPowerW(selections) : 0,
      powerPerMeter: selections.power,
      voltage: selections.voltage,
      cover: selections.cover,
      cableLength: selections.cableLength,
      cableExit: selections.cableExit,
      dimmerTouch: selections.dimmerTouch,
      powerSupply: selections.powerSupply,
      emissionOption: selections.emissionOption,
      notes: selections.notes,
      quantity,
      images: {
        // Foto nella finitura scelta (finishImages), altrimenti quella principale.
        imagePath: getFinishImage(selectedModel, selections.profileColor),
        techImagePath: selectedModel?.techImage,
      },
    };
  };

  const addConfiguredProfile = () => {
    if (!selections || !summary) {
      showWarningMessage(t('configurator.warningFillFields'));
      return;
    }

    const totalPowerW = getConfiguredTotalPowerW(selections);
    const wattsPerMeter = getPowerWattsPerMeter(selections.power);
    const powerPerMeterLabel = `${selections.emissionOption === 'biemissione' ? '2x' : ''}${wattsPerMeter}W/m`;
    const priceKey = getPowerPriceKey(selections.power, selections.voltage, selections.ledColor);
    const pricePerMeter = configData.powerPricesPerMeter[priceKey];

    if (pricePerMeter == null) {
      console.error(`Prezzo potenza non trovato per chiave: ${priceKey}`);
      showWarningMessage(t('configurator.warningLedPriceUnavailable'));
      return;
    }

    const datasheet = selectedModel?.datasheet || null;
    let datasheetConfig = null;
    try {
      datasheetConfig = buildPdfPayload();
    } catch (err) {
      console.error('Errore payload PDF:', err);
    }

    const row = {
      code: summary.code,
      description: `${selections.model}, ${selections.profileColor}, ${selections.length}cm, ${formatLedLabel(selections.ledColor)}, ${getEmissionLabel(selections.emissionOption)}, ${powerPerMeterLabel} (tot. ${totalPowerW}W), ${selections.voltage}, alim ${selections.powerSupply}, ${selections.cover}, ${t('configurator.cable').toLowerCase()} ${selections.cableLength}m`,
      unitPrice: summary.listinoUnit,
      quantity,
      notes: selections.notes,
      datasheet,
      datasheetType: datasheetConfig ? 'profileDynamic' : 'static',
      datasheetConfig
    };

    if (editing) {
      // "Aggiorna riga": stessa riga del carrello (posizione, zona e sconto restano), poi torna al carrello.
      replaceCartItem(editing.id, row);
      setEditing(null);
      playSound();
      navigate('/cart', { state: { updatedId: editing.id } });
      return;
    }

    addToCart(row);

    showMessage(`${t('configurator.messageProductAdded')} · ${summary.code} × ${quantity}`);
    playSound();
  };

  const handleGeneratePdf = async () => {
    const payload = buildPdfPayload();
    if (!payload) {
      showWarningMessage(t('configurator.warningCompleteBeforePdf'));
      return;
    }

    try {
      const response = await fetch('/api/products/datasheet/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        console.error('Errore nella risposta del server per il PDF');
        showWarningMessage(t('configurator.warningPdfGeneration'));
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${payload.code || 'datasheet_profilo'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore nella richiesta di generazione del PDF:', error);
      showWarningMessage(t('configurator.warningPdfCommunication'));
    }
  };

  const handleAddSuggestedPowerSupply = () => {
    const suggestedPowerSupply = summary?.suggestedPowerSupply;
    const suggestedRequiredW = summary?.suggestedRequiredW;
    if (!suggestedPowerSupply) {
      showWarningMessage(t('configurator.warningNoSuggestedPowerSupply'));
      return;
    }

    const code = suggestedPowerSupply.id || suggestedPowerSupply.code || suggestedPowerSupply.name || suggestedPowerSupply.power;
    const price = Number(suggestedPowerSupply.price);

    if (!code) {
      showWarningMessage(t('configurator.warningPowerSupplyCodeUnavailable'));
      return;
    }

    if (!Number.isFinite(price) || price <= 0) {
      showWarningMessage(t('configurator.warningPowerSupplyPriceUnavailable'));
      return;
    }

    const descPower = suggestedPowerSupply.power || suggestedPowerSupply.name || 'Power Supply 24V';
    const desc = `${descPower} 24V - suggested (${suggestedRequiredW ? t('configurator.requiresWatts', { watts: suggestedRequiredW }) : t('configurator.suggestedPowerSupplyFallback')})`;

    addToCart({
      code,
      description: desc,
      unitPrice: price,
      quantity: 1,
      datasheet: suggestedPowerSupply.datasheet,
      image: suggestedPowerSupply.image,
      notes: t('configurator.suggestedPowerSupplyNotes')
    });

    showMessage(t('configurator.messagePowerSupplyAdded'));
    playSound();
  };

  // Accessori del modello (modelAccessories in config.json): righe separate nel carrello,
  // costo a pezzo x4,5 come i profili; "prices" = costo per finitura, altrimenti "price".
  const modelAccessories = selectedModel ? configData.modelAccessories?.[selectedModel.name] || [] : [];
  const getAccessoryListPrice = (accessory, color) => {
    const cost = accessory.prices?.[color] ?? accessory.price;
    return cost == null ? null : Math.round(toNumber(cost) * 4.5 * 100) / 100;
  };

  const handleAddAccessory = (accessory) => {
    const qty = accessoryQuantities[accessory.key] || 0;
    const color = sel?.profileColor || '';
    const listPrice = getAccessoryListPrice(accessory, color);
    if (!selectedModel || listPrice == null || qty < 1) return;

    const colorCode = configData.profileColorCodes?.[color] || '';
    addToCart({
      code: `${getModelCode(configData, selectedModel.name)}-${accessory.key}${colorCode}`,
      description: `${accessory.name} ${selectedModel.name}${color ? `, ${getProfileColorLabel(color)}` : ''}`,
      unitPrice: listPrice,
      quantity: qty,
    });
    setAccessoryQuantities((prev) => ({ ...prev, [accessory.key]: 0 }));
    showMessage(t('configurator.messageProductAdded'));
    playSound();
  };

  const copyCode = () => {
    if (!summary?.code) return;
    try {
      navigator.clipboard?.writeText(summary.code).catch(() => {});
    } catch (e) {
      // copia non disponibile: il codice resta selezionabile a mano
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const jumpTo = (stepId) => {
    const el = stepRefs.current[stepId];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('is-ping');
    setTimeout(() => el.classList.remove('is-ping'), 900);
  };

  // ---------- Elenco profili ----------
  if (view === 'list' || !selectedModel || !sel) {
    return (
      <div className="cat pc">
        <section className="cat-band">
          <div className="cat-band-in">
            <div className="cat-head">
              <div>
                <h1>{t('configurator.customProfiles')}</h1>
                <p>{t('pcfg.listSubtitle')}</p>
              </div>
              <span className="cat-count">{t('configurator.profilesAvailable', { count: models.length })}</span>
            </div>
            <div className="cat-mounts pc-groups" role="group" aria-label={t('configurator.profileType')}>
              {Object.entries(GROUP_LABELS).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className="cat-mt"
                  aria-pressed={group === value}
                  onClick={() => setGroup(value)}
                >
                  <span>{label}</span>
                  <small>{groupCount(value)}</small>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="cat-grid-wrap">
          <div className="pc-list">
            {models.map((m) => {
              const features = [
                m.hasEmissionOption && t('configurator.biEmission'),
                m.hasDimmerTouchOption && t('configurator.dimmerTouch'),
                m.hasPowerSupplyOption && '230Vac',
              ].filter(Boolean);
              return (
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <div
                  key={m.name}
                  className="pc-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSelectModel(m)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectModel(m); } }}
                >
                  <span className="pc-ph">
                    <img src={urlAsset(getCustomProfileCardImage(m))} alt={m.name} loading="lazy" decoding="async" />
                    {/* Al passaggio del mouse la foto lascia il posto al disegno tecnico. */}
                    {m.techImage && (
                      <img src={urlAsset(m.techImage)} alt="" aria-hidden="true" className="is-tech" loading="lazy" decoding="async" />
                    )}
                  </span>
                  <span className="pc-body">
                    <span className="pc-grp">{m.groupLabel || GROUP_LABELS[m.groupKey]}</span>
                    <span className="pc-name">
                      <strong>{m.name}</strong>
                      {m.datasheet && (
                        <PdfButton
                          label={t('configurator.datasheet')}
                          onClick={(e) => { e.stopPropagation(); handleDownload(m.datasheet); }}
                        />
                      )}
                    </span>
                    <span className="pc-fin" title={m.availableFinishes?.map(getProfileColorLabel).join(' / ')}>
                      {(m.availableFinishes || []).map((f) => <i key={f} style={{ background: FINISH_COLORS[f] || '#fff' }} />)}
                    </span>
                    {features.length > 0 && (
                      <span className="pc-feat">{features.map((f) => <span key={f}>{f}</span>)}</span>
                    )}
                    <span className="pc-go">{t('configurator.configure')} →</span>
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    );
  }

  // ---------- Configurazione ----------
  const finishImage = getFinishImage(selectedModel, sel.profileColor);
  const shownImage = imageKind === 'tech' && selectedModel.techImage ? selectedModel.techImage : finishImage;
  const autoConfirmed = (id) =>
    (id === 'cover' && covers.length <= 1) ||
    (id === 'finish' && (selectedModel.availableFinishes || []).length <= 1) ||
    (id === 'psu' && sel.voltage === '230Vac');

  const steps = [];
  const addStep = (id, title, value, body) => {
    const ok = touched.has(id) || autoConfirmed(id);
    steps.push({ id, title, value, ok, body });
  };

  addStep('finish', t('configurator.finish'), getProfileColorLabel(sel.profileColor), (
    <div className="pc-opts">
      {(selectedModel.availableFinishes || []).map((f) => (
        <button key={f} type="button" className="pc-op" aria-pressed={sel.profileColor === f} onClick={() => { update('finish', { profileColor: f }); setImageKind('photo'); }}>
          <span className="pc-dot" style={{ background: FINISH_COLORS[f] || '#fff' }} />{getProfileColorLabel(f)}
        </button>
      ))}
    </div>
  ));
  if (selectedModel.hasEmissionOption) {
    addStep('emission', t('configurator.emission'), getEmissionLabel(sel.emissionOption), (
      <div className="pc-opts">
        <button type="button" className="pc-op" aria-pressed={sel.emissionOption === 'monoemissione'} onClick={() => update('emission', { emissionOption: 'monoemissione' })}>
          {t('configurator.monoEmission')} <small>{t('pcfg.monoHint')}</small>
        </button>
        <button type="button" className="pc-op" aria-pressed={sel.emissionOption === 'biemissione'} onClick={() => update('emission', { emissionOption: 'biemissione' })}>
          {t('configurator.biEmission')} <small>{t('pcfg.biHint')}</small>
        </button>
      </div>
    ));
  }
  addStep('led', t('configurator.ledColor'), formatLedLabel(sel.ledColor), (
    <div className="pc-opts">
      {ledColors.map((c) => (
        <button key={c} type="button" className="pc-op" aria-pressed={sel.ledColor === c} onClick={() => pickLedColor(c)}>
          <span className="pc-dot" style={{ background: LED_SWATCH[c] || '#fff' }} />{formatLedLabel(c)}
        </button>
      ))}
    </div>
  ));
  addStep('power', t('configurator.power'), sel.power ? `${sel.power} · ${sel.voltage}` : '—', (
    <div className="pc-opts">
      {powerOptions.map((o) => (
        <button
          key={`${o.power}|${o.voltage}`}
          type="button"
          className="pc-op"
          aria-pressed={sel.power === o.power && sel.voltage === o.voltage}
          onClick={() => update('power', { power: o.power, voltage: o.voltage })}
        >
          {o.power}{o.voltage === '230Vac' && <span className="pc-tag">230Vac</span>}
        </button>
      ))}
    </div>
  ));
  addStep('length', t('configurator.length'), Number.isFinite(lengthNumber) ? `${lengthNumber} cm` : '—', (
    <>
      <div className="pc-lenrow">
        <span className="pc-lenbox">
          <button type="button" onClick={() => stepLength(-5)} aria-label="-5 cm"><FiMinus aria-hidden="true" /></button>
          <input
            type="number"
            min="30"
            max={maxLength}
            step="5"
            value={sel.length}
            onChange={(e) => changeLength(e.target.value)}
            aria-label={t('configurator.length')}
          />
          <button type="button" onClick={() => stepLength(5)} aria-label="+5 cm"><FiPlus aria-hidden="true" /></button>
          <span>cm</span>
        </span>
        <input
          type="range"
          min="30"
          max={maxLength}
          step="5"
          value={Number.isFinite(lengthNumber) ? Math.min(maxLength, Math.max(30, lengthNumber)) : 30}
          onChange={(e) => changeLength(e.target.value)}
          aria-label={t('configurator.length')}
        />
        <span className="pc-lenmeta">{t('pcfg.maxLength', { max: maxLength })}</span>
      </div>
      <div className="pc-ruler" aria-hidden="true">
        <i style={{ width: `${Math.max(4, Math.min(100, ((Number.isFinite(lengthNumber) ? lengthNumber : 0) / maxLength) * 100))}%` }} />
      </div>
      <div className={`pc-lenmeta${lengthOk ? '' : ' is-err'}`}>
        {lengthOk ? t('pcfg.lengthRange', { max: maxLength }) : t('configurator.invalidLength', { max: maxLength })}
      </div>
    </>
  ));
  if (covers.length > 0) {
    addStep('cover', t('configurator.cover'), sel.cover || '—', (
      <div className="pc-opts">
        {covers.map((c) => (
          <button key={c} type="button" className="pc-op" aria-pressed={sel.cover === c} disabled={covers.length === 1} onClick={() => update('cover', { cover: c })}>
            {c}
          </button>
        ))}
        {covers.length === 1 && <span className="pc-lock">{t('pcfg.singleCover')}</span>}
      </div>
    ));
  }
  addStep('cable', `${t('configurator.cable')} · ${t('configurator.cableExit')}`, `${sel.cableLength} m · ${sel.cableExit}`, (
    <>
      <div className="pc-opts">
        {CABLES.map((c) => (
          <button key={c} type="button" className="pc-op" aria-pressed={sel.cableLength === c} onClick={() => update('cable', { cableLength: c })}>
            {c} m
          </button>
        ))}
      </div>
      <div className="pc-opts">
        {CABLE_EXITS.map((e) => (
          <button key={e} type="button" className="pc-op pc-exit" aria-pressed={sel.cableExit === e} onClick={() => update('cable', { cableExit: e })}>
            <ExitIcon exit={e} />{e}
          </button>
        ))}
      </div>
    </>
  ));
  if (dimmerAvailable) {
    addStep('dimmer', t('configurator.dimmerTouch'), sel.dimmerTouch === 'si' ? t('configurator.dimmerIncluded') : t('common.no'), (
      <div className="pc-opts">
        <button type="button" className="pc-op" aria-pressed={sel.dimmerTouch === 'no'} onClick={() => update('dimmer', { dimmerTouch: 'no' })}>{t('common.no')}</button>
        <button type="button" className="pc-op" aria-pressed={sel.dimmerTouch === 'si'} onClick={() => update('dimmer', { dimmerTouch: 'si' })}>{t('pcfg.dimmerYes')}</button>
      </div>
    ));
  }
  if (selectedModel.hasPowerSupplyOption) {
    const effectivePsu = sel.voltage === '230Vac' ? '230Vac' : sel.powerSupply;
    addStep('psu', t('configurator.powerSupply'), effectivePsu, (
      <div className="pc-opts">
        <button type="button" className="pc-op" aria-pressed={effectivePsu === '24V'} disabled={sel.voltage === '230Vac'} onClick={() => update('psu', { powerSupply: '24V' })}>
          24V <small>{t('pcfg.psu24Hint')}</small>
        </button>
        <button type="button" className="pc-op" aria-pressed={effectivePsu === '230Vac'} onClick={() => update('psu', { powerSupply: '230Vac' })}>
          230Vac <small>{t('pcfg.psu230Hint')}</small>
        </button>
        {sel.voltage === '230Vac' && <span className="pc-lock">{t('pcfg.strip230')}</span>}
      </div>
    ));
  }

  const todo = steps.filter((s) => !s.ok);
  const confirmedCount = steps.length - todo.length;

  const handleAddClick = () => {
    if (todo.length > 0 && !askConfirm) {
      setAskConfirm(true);
      return;
    }
    setAskConfirm(false);
    addConfiguredProfile();
  };

  return (
    <div className="cat pc">
      <div className="pc-wrap">
        <div className="pc-head">
          <div>
            <div className="pc-crumb">{t('configurator.customProfiles')} · {selectedModel.groupLabel || GROUP_LABELS[selectedModel.groupKey] || ''}</div>
            <h1>{selectedModel.name}</h1>
          </div>
          <div className="pc-head-acts">
            <button type="button" className="pc-btn" onClick={handleBackToList}>
              <FiChevronLeft aria-hidden="true" /> {t('configurator.backToProfiles')}
            </button>
          </div>
        </div>

        {editing && (
          <div className="pc-editbar">
            <FiEdit2 aria-hidden="true" />
            <span><b>{t('pcfg.editingRow')}</b> · <code>{editing.code}</code></span>
            <button type="button" className="pc-btn" onClick={cancelEdit}>{t('pcfg.cancelEdit')}</button>
          </div>
        )}

        <div className="pc-grid">
          <aside className="pc-panel pc-gal">
            <button type="button" className="pc-hero" onClick={() => setZoomSrc(shownImage)} aria-label={t('catalog.zoom')}>
              <img src={urlAsset(shownImage)} alt={selectedModel.name} />
              <span className="pc-zoomhint"><FiZoomIn aria-hidden="true" /></span>
            </button>
            <div className="pc-ths">
              <button type="button" className="pc-th" aria-pressed={imageKind !== 'tech'} onClick={() => setImageKind('photo')}>
                <img src={urlAsset(finishImage)} alt="" />
                {t('catalog.photo')} · {getProfileColorLabel(sel.profileColor)}
              </button>
              {selectedModel.techImage && (
                <button type="button" className="pc-th" aria-pressed={imageKind === 'tech'} onClick={() => setImageKind('tech')}>
                  <img src={urlAsset(selectedModel.techImage)} alt="" />
                  {t('configurator.technicalDrawing')}
                </button>
              )}
            </div>
          </aside>

          <div className="pc-steps">
            {steps.map((s, i) => (
              <section
                key={s.id}
                ref={(el) => { stepRefs.current[s.id] = el; }}
                className={`pc-panel pc-step ${s.ok ? 'is-ok' : 'is-def'}`}
              >
                <div className="pc-shd">
                  <span className="pc-n">{s.ok ? '✓' : i + 1}</span>
                  <h3>{s.title}</h3>
                  {!s.ok && <span className="pc-def">{t('pcfg.default')}</span>}
                  <span className="pc-val">{s.value}</span>
                </div>
                {s.body}
              </section>
            ))}

            {modelAccessories.length > 0 && (
              <section className="pc-panel pc-step" ref={(el) => { stepRefs.current.accessories = el; }}>
                <div className="pc-shd">
                  <span className="pc-n">+</span>
                  <h3>{t('looseProfiles.accessories')} {selectedModel.name}</h3>
                  <span className="pc-val">{t('pcfg.accessoriesHint')}</span>
                </div>
                <div className="pc-acc">
                  {modelAccessories.map((accessory) => {
                    const listPrice = getAccessoryListPrice(accessory, sel.profileColor);
                    const qty = accessoryQuantities[accessory.key] || 0;
                    return (
                      <div key={accessory.key} className="pc-accit">
                        {accessory.image && (
                          <button type="button" className="pc-accimg" onClick={() => setZoomSrc(accessory.image)} aria-label={t('catalog.zoom')}>
                            <img src={urlAsset(accessory.image)} alt={accessory.name} />
                          </button>
                        )}
                        <b>{accessory.name}</b>
                        {showPrices && listPrice != null && (
                          <span className="pc-accp">{formatEuro(listPrice)} · {getProfileColorLabel(sel.profileColor)}</span>
                        )}
                        <div className="pc-accr">
                          <input
                            type="number"
                            min="0"
                            value={qty}
                            aria-label={t('configurator.quantity')}
                            onChange={(e) =>
                              setAccessoryQuantities((prev) => ({
                                ...prev,
                                [accessory.key]: Math.max(0, parseInt(e.target.value, 10) || 0),
                              }))
                            }
                          />
                          <button type="button" onClick={() => handleAddAccessory(accessory)} disabled={listPrice == null || qty < 1}>
                            {t('looseProfiles.addAccessory')}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>

          <aside className="pc-panel pc-sum">
            <h3>{t('configurator.summary')}</h3>

            <div className={`pc-prog${todo.length ? '' : ' is-all'}`}>
              <div className="pc-pt">
                <b>{t('pcfg.confirmedOf', { n: confirmedCount, total: steps.length })}</b>
                {!todo.length && <span>{t('pcfg.allChecked')}</span>}
              </div>
              <div className="pc-pb"><i style={{ width: `${steps.length ? (confirmedCount / steps.length) * 100 : 0}%` }} /></div>
              {todo.length > 0 && (
                <div className="pc-pl">
                  {t('pcfg.toCheck')}
                  {todo.map((s) => <button key={s.id} type="button" onClick={() => jumpTo(s.id)}>{s.title}</button>)}
                </div>
              )}
            </div>

            <div className="pc-code">
              <span>{t('configurator.code')}</span>
              <code className={flashCode ? 'is-flash' : ''}>{summary ? summary.code : '—'}</code>
              <button type="button" className="pc-copy" onClick={copyCode} disabled={!summary}>
                <FiCopy aria-hidden="true" /> {copied ? t('pcfg.copied') : t('pcfg.copy')}
              </button>
            </div>

            <div className="pc-rows">
              <div><span>{t('pcfg.totalPower')}</span><b>{summary ? `${summary.totalPowerW} W` : '—'}</b></div>
              <div><span>{t('pcfg.strip')}</span><b>{sel.power ? `${sel.power} · ${sel.voltage}` : '—'}</b></div>
              <div><span>{t('configurator.cableExit')}</span><b>{sel.cableExit}</b></div>
            </div>

            {showPrices ? (
              <div className="pc-pbox">
                {summary ? (
                  <>
                    <span className="pc-l">{t('configurator.listPrice')} <s>{formatEuro(summary.listinoUnit)}</s></span>
                    <span className="pc-net">
                      {formatEuro(summary.netPrice)}
                      {toNumber(user?.discount) > 0 && <small>−{toNumber(user?.discount)}%</small>}
                    </span>
                    <span className="pc-w">{t('pcfg.netPerPiece')}</span>
                  </>
                ) : (
                  <span className="pc-w">{t('configurator.completeConfigurationSummary')}</span>
                )}
              </div>
            ) : (
              <div className="pc-pbox"><span className="pc-w">{t('configurator.prices')}: {t('common.hidden')}</span></div>
            )}

            {!lengthOk && <p className="pc-warn">{t('pcfg.fixLength')}</p>}

            {askConfirm && todo.length > 0 && (
              <div className="pc-ask">
                <b>{todo.length === 1 ? t('pcfg.askOne') : t('pcfg.askMany', { n: todo.length })}</b>
                <ul>{todo.map((s) => <li key={s.id}>{s.title}: <strong>{s.value}</strong></li>)}</ul>
                <div className="pc-askr">
                  <button type="button" className="is-check" onClick={() => jumpTo(todo[0].id)}>{t('pcfg.check')}</button>
                  <button type="button" className="is-anyway" onClick={() => { setAskConfirm(false); addConfiguredProfile(); }}>{t('pcfg.addAnyway')}</button>
                </div>
              </div>
            )}

            <div className="pc-addrow">
              <QtyStepper value={quantity} onChange={(v) => setQuantity(Math.max(1, parseInt(v, 10) || 1))} label={t('configurator.quantity')} />
              <button type="button" className="pc-cta" onClick={handleAddClick} disabled={!summary}>
                {editing
                  ? <><FiCheck aria-hidden="true" /> {t('pcfg.updateRow')}</>
                  : <><FiShoppingCart aria-hidden="true" /> {t('configurator.addToCart')}</>}
              </button>
            </div>
            {warningMessage ? <div className="pc-warn">{warningMessage}</div> : null}

            {modelAccessories.length > 0 && (
              <button type="button" className="pc-acclink" onClick={() => jumpTo('accessories')}>
                <span>{t('pcfg.accessoriesAvailable', { n: modelAccessories.length })}</span><span aria-hidden="true">↓</span>
              </button>
            )}
            <span className="pc-pdfcfg">
              <PdfButton onClick={handleGeneratePdf} label={t('pcfg.pdfThis')} />
            </span>

            {summary?.suggestedPowerSupply && (
              <div className="pc-psu">
                <b>{t('configurator.suggestedPowerSupply')}</b>
                <div className="pc-psur">
                  <span>
                    {(summary.suggestedPowerSupply.id || summary.suggestedPowerSupply.name || 'Power Supply 24V')}
                    {' · '}{summary.suggestedPowerSupply.power}
                    {summary.suggestedRequiredW ? <small> ({t('configurator.requiresWatts', { watts: summary.suggestedRequiredW })})</small> : null}
                  </span>
                  <button type="button" onClick={handleAddSuggestedPowerSupply}>{t('configurator.addPowerSupply')}</button>
                </div>
              </div>
            )}

            <div className="pc-imp">
              <button type="button" className="pc-impsum" aria-expanded={importOpen} onClick={() => setImportOpen((o) => !o)}>
                {importOpen ? '▾' : '▸'} {t('pcfg.haveCode')}
              </button>
              {importOpen && (
                <>
                  <div className="pc-improw">
                    <input
                      type="text"
                      value={codeImportValue}
                      onChange={(e) => {
                        setCodeImportValue(e.target.value);
                        setCodeImportMessage('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          applyDecodedCode();
                        }
                      }}
                      placeholder="Incolla codice profilo"
                      aria-label="Esplodi codice"
                    />
                    <button type="button" onClick={applyDecodedCode}>{t('pcfg.apply')}</button>
                  </div>
                  {codeImportMessage ? (
                    <p className={`pc-impmsg${codeImportMessage.includes('applicato') ? ' is-ok' : ' is-err'}`}>{codeImportMessage}</p>
                  ) : null}
                </>
              )}
            </div>

            <label className="pc-notes">
              <span>{t('pcfg.productionNotes')}</span>
              <textarea
                value={notes}
                placeholder={t('common.notesPlaceholder')}
                onChange={(e) => setNotes(e.target.value)}
              />
            </label>

            <button type="button" className="pc-btn pc-gocart" onClick={() => navigate('/cart')}>
              {t('configurator.viewCart')}
            </button>
          </aside>
        </div>
      </div>

      {zoomSrc && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div className="cat-zoom" onClick={() => setZoomSrc(null)}>
          <img src={urlAsset(zoomSrc)} alt="Zoom" />
        </div>
      )}

      <AddedToast message={successMessage} onGoToCart={() => navigate('/cart')} goToCartLabel={t('shop.goToCart')} />
    </div>
  );
};

export default Configurator;
